// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, change } from 'redux-form';
import InputField from '../../layout/reduxForm/customFields/InputField';
import ReactSelectField from '../../layout/reduxForm/customFields/ReactSelectField';
import { fetchCountries, fetchStatesOfCountry } from '../../../storage/actions/workOrdersActions';
import findOption from '../../../utils/addressSection/findOption';
import handlerValidateZipCode from '../../../utils/addressSection/handlerValidateZipCode';
import Notification from '../../../components/notification';

const createWorkOrderReduxFormSelector = formValueSelector('createWorkOrderReduxForm');

// --- Validation helpers ---
const specialCharRegex = /^[a-zA-Z0-9\s.,'-]*$/;

const validateNoSpecialChars = (value, fieldName) => {
    if (!value) return undefined;
    const isValid = specialCharRegex.test(value);
    if (!isValid) {
        Notification('warning', { message: `${fieldName} contains special characters!` });
        return 'Special characters are not allowed';
    }
    return undefined;
};

const AddressSection = ({
                            selectedState,
                            country,
                            change,
                            fetchCountries,
                            fetchStatesOfCountry,
                            isRemote
                        }) => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);

    const getStates = async countryId => {
        const newStates = await fetchStatesOfCountry(countryId);
        setStates(newStates);
        if (!newStates.some(item => (
            item.isoCode === selectedState?.value
            || item.isoCode === selectedState?.label
            || item.label === selectedState?.label
        ))) {
            change('createWorkOrderReduxForm', 'state', '');
        } else if (!selectedState?.value || selectedState?.label) {
            const newState = findOption(newStates, ['label', 'value'], selectedState?.label, selectedState);
            change('createWorkOrderReduxForm', 'state', newState);
        }
    };

    const getCountries = async () => {
        const newCountriesList = await fetchCountries();
        const newCountries = newCountriesList.map(item => ({
            ...item, value: item.isoCode, label: item.name,
        }));
        setCountries(newCountries);
        if (newCountries.length && !country?.value && !country?.label) {
            const newCountry = findOption(newCountries, ['name'], 'United States');
            change('createWorkOrderReduxForm', 'country', newCountry);
        } else if (newCountries.length && !country?.value && country?.label) {
            const newCountry = findOption(newCountries, ['name'], country?.label, country);
            change('createWorkOrderReduxForm', 'country', newCountry);
        }
    };

    useEffect(() => {
        if (!countries.length) getCountries();
        if (country?.value) getStates(country.value);
    }, [country]);

    return (
        <div className="fields_group">
            <div className="group_subtitle">Site Address</div>

            <div className="field_col">
                <label className="field_name" htmlFor="site_address">Street Address{!isRemote ? '*' : ''}</label>
                <div className="field_block">
                    <Field
                        name="address1"
                        component={InputField}
                        type="text"
                        placeholder="Street Address"
                        label=""
                        required={!isRemote}
                        className="flex-column font-size-08"
                        inputClassName="form-control custom-input-style"
                        disabled={isRemote}
                        id="site_address"
                        validate={[
                            value => validateNoSpecialChars(value, 'Street Address')
                        ]}
                    />
                </div>
            </div>

            <div className="field_col">
                <label className="field_name" htmlFor="address_line_2">Bldg, Suite, Etc.</label>
                <div className="field_block">
                    <Field
                        name="address2"
                        component={InputField}
                        type="text"
                        disabled={isRemote}
                        placeholder="Bldg, Suite, Etc."
                        label=""
                        className="flex-column font-size-08"
                        inputClassName="form-control custom-input-style"
                        id="address_line_2"
                        validate={[
                            value => validateNoSpecialChars(value, 'Bldg, Suite, Etc.')
                        ]}
                    />
                </div>
            </div>

            <div className="field_col">
                <label className="field_name" htmlFor="city">City{!isRemote ? '*' : ''}</label>
                <div className="field_block">
                    <Field
                        name="city"
                        component={InputField}
                        type="text"
                        disabled={isRemote}
                        placeholder="City"
                        label=""
                        required={!isRemote}
                        className="flex-column font-size-08"
                        inputClassName="form-control custom-input-style"
                        id="city"
                        validate={[
                            value => validateNoSpecialChars(value, 'City')
                        ]}
                    />
                </div>
            </div>

            <div className="field_col">
                <label className="field_name" htmlFor="state">State{!isRemote ? '*' : ''}</label>
                <Field
                    name="state"
                    component={ReactSelectField}
                    className="flex-column"
                    placeholder="State"
                    options={states}
                    required={!isRemote}
                    disabled={isRemote}
                    id="state"
                />
            </div>

            <div className="field_col">
                <label className="field_name" htmlFor="zip_code">Zip / Postal Code{!isRemote ? '*' : ''}</label>
                <div className="field_block">
                    <Field
                        name="zipCode"
                        component={InputField}
                        type="text"
                        placeholder="Zip / Postal Code"
                        label=""
                        required={!isRemote}
                        disabled={isRemote}
                        className="flex-column font-size-08"
                        inputClassName="form-control custom-input-style"
                        validate={value => handlerValidateZipCode(value, country)}
                        id="zip_code"
                    />
                </div>
            </div>

            <div className="field_col">
                <label className="field_name" htmlFor="country_select">Country{!isRemote ? '*' : ''}</label>
                <Field
                    name="country"
                    component={ReactSelectField}
                    className="flex-column"
                    placeholder="Country"
                    options={countries}
                    required={!isRemote}
                    disabled={isRemote}
                    id="country_select"
                />
            </div>

        </div>
    );
};

const mapDispatchToProps = {
    change,
    fetchCountries,
    fetchStatesOfCountry,
};

const mapStateToProps = state => ({
    country: createWorkOrderReduxFormSelector(state, 'country'),
    selectedState: createWorkOrderReduxFormSelector(state, 'state'),
    isRemote: createWorkOrderReduxFormSelector(state, 'isRemote'),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddressSection);
